import { computed, ref, unref, watch } from 'vue';
import { debounce } from '@chatwoot/utils';

/**
 * Local search input with a debounced filter query.
 * Typing updates `searchQuery` immediately; `filteredOptions` waits `delay` ms.
 *
 * @param {import('vue').MaybeRefOrGetter<Array>} options
 * @param {(item: object, query: string) => boolean} matchFn
 * @param {number} [delay=500]
 */
export function useDebouncedFilter(options, matchFn, delay = 500) {
  const searchQuery = ref('');
  const debouncedQuery = ref('');

  const flushDebouncedQuery = debounce(() => {
    debouncedQuery.value = searchQuery.value;
  }, delay);

  watch(searchQuery, () => {
    flushDebouncedQuery();
  });

  const filteredOptions = computed(() => {
    const list = unref(options) || [];
    const query = debouncedQuery.value.trim().toLowerCase();
    if (!query) return list;
    return list.filter(item => matchFn(item, query));
  });

  const resetSearch = () => {
    searchQuery.value = '';
    debouncedQuery.value = '';
  };

  return {
    searchQuery,
    debouncedQuery,
    filteredOptions,
    resetSearch,
  };
}
